import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../contexts/PaymentContext';
import { THEME_COLORS, THEME_GRADIENTS, THEME_RADIUS } from '../constants/theme';

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getSessionDetails } = usePayment();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessionData = async () => {
      const sessionId = searchParams.get('session_id');
      const tripId = searchParams.get('trip_id');
      const bookingRef = searchParams.get('booking_ref');
      const partnerPlan = searchParams.get('partner_plan');
      
      if (!sessionId) {
        // If no session ID, show a generic success message
        setSessionData({
          paymentStatus: 'paid',
          tripTitle: 'Your Trip',
          upgradeType: 'premium'
        });
        setLoading(false);
        return;
      }

      try {
        // Package bookings — confirm payment + create itinerary if webhook hasn't yet
        if (bookingRef) {
          try {
            const api = (await import('../config/api')).default;
            const verify = await api.post('/payments/verify-payment', { sessionId });
            setSessionData({
              paymentStatus: 'paid',
              tripTitle: 'Your Package Booking',
              upgradeType: 'partner_package',
              tripId: verify.data?.tripId || null,
              bookingReference: bookingRef,
            });
            setLoading(false);
            return;
          } catch (verifyErr) {
            console.error('Package payment verify on success page failed:', verifyErr);
          }
        }

        if (partnerPlan) {
          try {
            const api = (await import('../config/api')).default;
            const verify = await api.post('/payments/verify-payment', { sessionId });
            setSessionData({
              paymentStatus: 'paid',
              tripTitle: 'Partner Membership',
              upgradeType: 'partner_plan',
              partnerPlan: verify.data?.partnerPlan,
            });
            setLoading(false);
            return;
          } catch (verifyErr) {
            console.error('Partner plan verify on success page failed:', verifyErr);
            setSessionData({
              paymentStatus: 'paid',
              tripTitle: 'Partner Membership',
              upgradeType: 'partner_plan'
            });
            setLoading(false);
            return;
          }
        }

        const data = await getSessionDetails(sessionId);
        setSessionData(data);
      } catch (err) {
        console.error('Error fetching session data:', err);
        // Show a generic success message even if we can't fetch details
        setSessionData({
          paymentStatus: 'paid',
          tripTitle: 'Your Trip',
          upgradeType: 'premium',
          tripId: tripId
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [searchParams, getSessionDetails]);

  const handleContinue = () => {
    const tripId = searchParams.get('trip_id') || sessionData?.tripId;
    const upgradeType = sessionData?.upgradeType;
    const bookingRef = searchParams.get('booking_ref') || sessionData?.bookingReference;
    
    if (tripId && tripId !== 'ai_concierge') {
      sessionStorage.setItem(`payment_completed_trip_${tripId}`, 'true');
    }

    if (upgradeType === 'partner_plan' || searchParams.get('partner_plan')) {
      navigate('/partner/billing');
      return;
    }

    if (upgradeType === 'partner_package' || bookingRef) {
      if (tripId) {
        navigate(`/trips/${tripId}`);
      } else {
        navigate('/my-bookings');
      }
      return;
    }
    
    // Handle AI concierge payments (no trip_id or trip_id is 'ai_concierge')
    if (!tripId || tripId === 'ai_concierge') {
      navigate('/ai-concierge?payment=success');
    } else if (tripId) {
      // Regular trip upgrade payments - navigate to the specific trip
      navigate(`/trips/${tripId}`);
    } else {
      // Fallback to home page with a success message
      navigate('/?payment=success');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress size={60} sx={{ color: THEME_COLORS.primary.main }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Verifying your payment...
          </Typography>
        </Box>
      </Container>
    );
  }


  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card sx={{ 
        borderRadius: THEME_RADIUS.large,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <Box sx={{ 
          background: THEME_GRADIENTS.primary,
          color: 'white',
          p: 4,
          textAlign: 'center'
        }}>
          <CheckCircleIcon sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Payment Successful!
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Your premium features have been activated
          </Typography>
        </Box>

        {/* Content */}
        <CardContent sx={{ p: 4 }}>
          {sessionData && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Payment Details:
              </Typography>
              
              <Box sx={{ 
                bgcolor: '#f8f9fa', 
                p: 2, 
                borderRadius: THEME_RADIUS.small,
                mb: 2
              }}>
                {!searchParams.get('trip_id') ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Service:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      AI Concierge Service
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Access:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      Unlimited AI-powered trip planning
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Trip:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {sessionData.tripTitle || 'Your Trip'}
                      {sessionData.tripId && sessionData.tripId !== 'ai_concierge' && (
                        <> (#{sessionData.tripId})</>
                      )}
                    </Typography>

                    {sessionData.tripUrl && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Trip link:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                          <a href={sessionData.tripUrl} style={{ color: '#667eea', textDecoration: 'none' }}>
                            {sessionData.tripUrl}
                          </a>
                        </Typography>
                      </>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Plan:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {sessionData.upgradeType === 'passport'
                        ? t('plans.passport.name')
                        : t('plans.trip_pass.name')}
                    </Typography>
                  </>
                )}
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Status:
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: 'success.main'
                }}>
                  ✓ {sessionData.paymentStatus === 'paid' ? 'Paid' : 'Processing'}
                </Typography>
              </Box>
            </Box>
          )}

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {!searchParams.get('trip_id')
              ? t('paymentSuccess.aiConciergeBody')
              : sessionData?.upgradeType === 'passport'
                ? t('paymentSuccess.passportBody')
                : t('paymentSuccess.tripPassBody')}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleContinue}
            endIcon={<ArrowForwardIcon />}
            sx={{
              background: THEME_GRADIENTS.primary,
              py: 1.5,
              borderRadius: THEME_RADIUS.medium,
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            {!searchParams.get('trip_id')
              ? t('paymentSuccess.startAiConcierge')
              : t('paymentSuccess.continueToTrip')}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentSuccess;
